import Link from 'next/link';
import { memo, useMemo } from 'react';

import {
  FileStatusButton,
  FileStatusIconCompleted,
  FileStatusSubtitle,
  FileStatusTitle,
} from '@air/feature-file-status/ui';
import { Close } from '@air/next-icons';

import { PaneItemTW } from '~/components/FileStatusTrackingPane/PaneItemTW';
import { type ClipVideoTaskMetadata } from '~/components/VideoClipTasks/types';
import { createAllContentAssetRoute, createBoardClipRoute } from '~/constants/routes';

export type VideoClipTaskPanelItemCompletedProps = {
  metadata: ClipVideoTaskMetadata;
  onClear: () => void;
};

export const VideoClipTaskPanelItemCompleted = memo(({ metadata, onClear }: VideoClipTaskPanelItemCompletedProps) => {
  const clipId = metadata.versionId;
  const boardId = metadata.saveResultAs?.options?.boardId;

  const clipRoute = useMemo(() => {
    if (!clipId) {
      return undefined;
    }

    if (boardId) {
      return createBoardClipRoute({ clipId, boardId, boardTitle: '' });
    }

    return createAllContentAssetRoute(clipId);
  }, [clipId, boardId]);

  return (
    <PaneItemTW
      avatar={<FileStatusIconCompleted />}
      title={
        <FileStatusTitle data-testid="VIDEO_CLIP_PANEL_COMPLETED_TITLE">
          {metadata?.saveResultAs?.as === 'assetVersion' ? 'New version saved' : 'New video created'}
        </FileStatusTitle>
      }
      subtitle={
        <FileStatusSubtitle className="truncate">
          {clipRoute ? (
            <Link
              href={clipRoute}
              className="font-semibold text-12 text-altitude-9 hover:text-altitude-11 hover:underline"
            >
              View asset
            </Link>
          ) : null}
        </FileStatusSubtitle>
      }
      buttons={
        <FileStatusButton label="Dismiss" onClick={onClear}>
          <Close className="size-4" />
        </FileStatusButton>
      }
    />
  );
});

VideoClipTaskPanelItemCompleted.displayName = 'VideoClipTaskPanelItemCompleted';
